import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Archive, ArchiveRestore, FilePlus2, Trash2, Undo2 } from "lucide-react";

import { DEFAULT_POLICY, calcEstimate } from "@/calc";
import { taxScheme } from "@/braining/mocks";
import { STATUS_LABEL, TRASH_DAYS, estimateStatus, type BrainingEstimate } from "@/braining/types";
import { useEstimates } from "@/braining/store";
import { counted, pct, rub } from "@/braining/format";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute("/")({ component: EstimatesList });

type Tab = "active" | "archive" | "trash";

function EstimatesList() {
  const { estimates, ratecard, library, ready, saving, error, create } = useEstimates();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("active");
  const [creating, setCreating] = useState(false);

  async function onCreate() {
    if (creating) return;
    setCreating(true);
    try {
      // Тип проекта не спрашиваем: он определяется из материалов на первом шаге.
      const fresh = await create(library.projectTypes[0].id, { title: "", client: "" });
      await navigate({ to: "/estimates/$id", params: { id: fresh.id } });
    } catch (e) {
      console.error("[estimates] не удалось создать", e);
      toast.error("Не удалось создать калькулятор");
    } finally {
      setCreating(false);
    }
  }

  const buckets: Record<Tab, BrainingEstimate[]> = {
    active: estimates.filter((e) => !e.trashedAt && !e.archivedAt),
    archive: estimates.filter((e) => !e.trashedAt && e.archivedAt),
    trash: estimates.filter((e) => e.trashedAt),
  };
  const shown = buckets[tab];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Калькуляторы</h1>
          {/* Раньше здесь стояло «справочник собран из реальных шаблонов» —
              фраза с показа, которая объясняла происхождение данных людям,
              видевшим систему впервые. В работе она не нужна: сколько чего в
              справочнике, видно на самих экранах справочника. */}
          <p className="mt-1 text-sm text-muted-foreground">
            Сметы проектов: черновики, отправленные на проверку и утверждённые.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {saving && <span className="text-xs text-muted-foreground">сохраняем…</span>}
          <Button onClick={onCreate} disabled={creating}>
            <FilePlus2 className="size-4" />
            {creating ? "Создаём…" : "Новый калькулятор"}
          </Button>
        </div>
      </div>

      {error && (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink">
          {error}
        </p>
      )}

      {/* Работа и хранилища разведены намеренно: «В работе» — это сам экран,
          а архив с корзиной служебные и отжаты вправо, чтобы не выглядеть
          равноправными вкладками. */}
      <nav className="flex flex-wrap items-center gap-x-1 gap-y-2 border-b">
        <TabButton
          label="В работе"
          count={buckets.active.length}
          active={tab === "active"}
          onClick={() => setTab("active")}
        />
        <span className="ml-auto flex items-center gap-1">
          <TabButton
            label="Архив"
            icon={Archive}
            count={buckets.archive.length}
            active={tab === "archive"}
            muted
            onClick={() => setTab("archive")}
          />
          <TabButton
            label="Корзина"
            icon={Trash2}
            count={buckets.trash.length}
            active={tab === "trash"}
            muted
            onClick={() => setTab("trash")}
          />
        </span>
      </nav>

      {tab !== "active" && <SectionNote tab={tab} onBack={() => setTab("active")} />}

      {!ready ? (
        <p className="text-sm text-muted-foreground">Загружаем…</p>
      ) : shown.length === 0 ? (
        <EmptyState tab={tab} onCreate={onCreate} />
      ) : (
        <div className="grid gap-2">
          {shown.map((e) => (
            <EstimateRow key={e.id} estimate={e} tab={tab} />
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({
  label,
  count,
  active,
  muted,
  icon: Icon,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  muted?: boolean;
  icon?: typeof Archive;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 transition-colors",
        muted ? "text-xs" : "text-sm",
        active
          ? "border-primary font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {Icon && <Icon className="size-3.5" />}
      {label}
      {count > 0 && (
        <span className={cn("tabular-nums", active ? "text-foreground" : "text-muted-foreground")}>
          {count}
        </span>
      )}
    </button>
  );
}

/** Полоса «вы не в рабочем списке»: из архива и корзины должен быть виден выход. */
function SectionNote({ tab, onBack }: { tab: Exclude<Tab, "active">; onBack: () => void }) {
  const text =
    tab === "archive"
      ? "Архив: калькуляторы, которые не нужны в работе, но нужны в истории. Считаются и открываются как обычно."
      : `Корзина: удалённые калькуляторы лежат ${TRASH_DAYS} дней, потом чистятся. Открыть можно только после возврата.`;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border-l-2 px-3 py-2 text-sm",
        tab === "archive"
          ? "border-muted-foreground/40 bg-muted/40 text-muted-foreground"
          : "border-warning/50 bg-warning/10 text-warning-ink",
      )}
    >
      {tab === "archive" ? (
        <Archive className="size-4 shrink-0" />
      ) : (
        <Trash2 className="size-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1">{text}</span>
      <button
        type="button"
        onClick={onBack}
        className="shrink-0 underline underline-offset-2 hover:no-underline"
      >
        К работе
      </button>
    </div>
  );
}

function EstimateRow({ estimate, tab }: { estimate: BrainingEstimate; tab: Tab }) {
  const { archive, trash, restore, purge, ratecard, library } = useEstimates();

  const calc = calcEstimate(estimate, {
    ratecard,
    scheme: taxScheme(estimate.taxSchemeId, library),
    policy: DEFAULT_POLICY,
  });
  const type = library.projectTypes.find((p) => p.id === estimate.projectType);
  const blocks = estimate.stages.reduce((s, st) => s + st.blocks.length, 0);
  const daysLeft = estimate.trashedAt ? trashDaysLeft(estimate.trashedAt) : 0;
  const status = estimateStatus(estimate);

  // Раскладка без переноса: имя ужимается, остальное держит свою ширину.
  // На flex-wrap кнопки строки уезжали на второй ряд у одних строк и
  // оставались на месте у других — выглядело как сбой вёрстки.
  const body = (
    <CardContent className="flex items-center gap-4 py-4">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{estimate.title || "Без названия"}</div>
        <div className="mt-0.5 truncate text-sm text-muted-foreground">
          {estimate.client || "клиент не указан"}
          {type ? ` · ${type.name}` : ""}
        </div>
      </div>

      <div className="hidden shrink-0 items-center gap-2 sm:flex">
        {status === "approved" ? (
          <Badge variant="outline" className="border-success/40 text-success-ink">
            {STATUS_LABEL.approved}
          </Badge>
        ) : status === "review" ? (
          <Badge variant="outline" className="border-warning/40 text-warning-ink">
            {STATUS_LABEL.review}
          </Badge>
        ) : status === "changes" ? (
          <Badge variant="outline" className="border-destructive/40 text-destructive-ink">
            {STATUS_LABEL.changes}
          </Badge>
        ) : (
          <Badge variant="secondary">{STATUS_LABEL.draft}</Badge>
        )}
        <Badge variant="outline">{estimate.division}</Badge>
        <span className="hidden text-sm text-muted-foreground lg:inline">
          {counted(blocks, "блок", "блока", "блоков")}
        </span>
      </div>

      <div className="shrink-0 text-right">
        <div className="font-semibold tabular-nums">{rub(calc.totalGross)}</div>
        <div className="text-xs tabular-nums text-muted-foreground">
          {/* Прочерк, а не формальные 100%: расход ещё не заведён */}с НДС · рент.{" "}
          {calc.cost > 0 ? pct(calc.margin) : "—"}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {tab === "trash" ? (
          <>
            <span className="hidden pr-1 text-xs text-warning-ink sm:inline">
              {counted(daysLeft, "день", "дня", "дней")}
            </span>
            <IconButton title="Вернуть из корзины" onClick={() => restore(estimate.id)}>
              <Undo2 className="size-4" />
            </IconButton>
            <IconButton title="Удалить навсегда" danger onClick={() => purge(estimate.id)}>
              <Trash2 className="size-4" />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              title={estimate.archivedAt ? "Вернуть из архива" : "В архив"}
              onClick={() => archive(estimate.id, !estimate.archivedAt)}
            >
              {estimate.archivedAt ? (
                <ArchiveRestore className="size-4" />
              ) : (
                <Archive className="size-4" />
              )}
            </IconButton>
            <IconButton title="В корзину" onClick={() => trash(estimate.id)}>
              <Trash2 className="size-4" />
            </IconButton>
          </>
        )}
      </div>
    </CardContent>
  );

  // Из корзины калькулятор не открывается: сначала вернуть.
  if (tab === "trash") {
    return <Card className="border-l-2 border-l-warning/50 opacity-75">{body}</Card>;
  }

  return (
    <Link to="/estimates/$id" params={{ id: estimate.id }}>
      <Card
        className={cn(
          "transition-colors hover:border-primary/40",
          // Архивная строка помечена полосой, а не только блёклостью: полупрозрачный
          // текст читается как «загружается», а не как «убрано из работы».
          estimate.archivedAt && "border-l-2 border-l-muted-foreground/40 bg-muted/30",
        )}
      >
        {body}
      </Card>
    </Link>
  );
}

function IconButton({
  title,
  danger,
  onClick,
  children,
}: {
  title: string;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        // Строка обёрнута в ссылку — иначе нажатие уведёт в смету.
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary",
        danger ? "hover:text-destructive" : "hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({ tab, onCreate }: { tab: Tab; onCreate: () => void }) {
  const text: Record<Tab, string> = {
    active: "Калькуляторов в работе нет.",
    archive: "Архив пуст. Сюда убирают то, что не нужно в работе, но нужно в истории.",
    trash: "Корзина пуста.",
  };

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-sm text-muted-foreground">{text[tab]}</p>
        {tab === "active" && (
          <Button variant="outline" onClick={onCreate}>
            <FilePlus2 className="size-4" />
            Создать первый
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function trashDaysLeft(trashedAt: string): number {
  const passed = (Date.now() - new Date(trashedAt).getTime()) / 86_400_000;
  return Math.max(0, Math.ceil(TRASH_DAYS - passed));
}
